import React, { useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { useTheme } from "@/context/ThemeContext";
import { Sun, Moon } from "lucide-react";

const JORDAN_CENTER = [31.9454, 35.9284];

function makeMarkerIcon(isDark) {
  return L.divIcon({
    className: "",
    html: `<div style="width:32px;height:32px;background:#CCFF00;border:3px solid ${isDark ? "#0A0A0A" : "#FFFFFF"};box-shadow:0 0 24px rgba(204,255,0,0.5);transform:rotate(45deg);display:flex;align-items:center;justify-content:center;"><div style="width:8px;height:8px;background:#0A0A0A;transform:rotate(-45deg);"></div></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

const DARK_TILES = "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";
const LIGHT_TILES = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

function ClickHandler({ onClick }) {
  const clickTimer = useRef(null);

  useMapEvents({
    dblclick(e) {
      if (clickTimer.current) {
        clearTimeout(clickTimer.current);
        clickTimer.current = null;
      }
    },
    click(e) {
      if (clickTimer.current) {
        clearTimeout(clickTimer.current);
        clickTimer.current = null;
        return;
      }
      clickTimer.current = setTimeout(() => {
        onClick([e.latlng.lat, e.latlng.lng]);
        clickTimer.current = null;
      }, 250);
    },
  });
  return null;
}

function MapCenterUpdater({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) {
      map.setView([lat, lng], map.getZoom());
    }
  }, [lat, lng, map]);
  return null;
}

export default function MapPicker({ lat, lng, onChange }) {
  const hasPos = lat != null && lng != null;
  const position = hasPos ? [lat, lng] : JORDAN_CENTER;
  const zoom = hasPos ? 15 : 7;
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  const handleClick = ([newLat, newLng]) => {
    onChange(newLat, newLng);
  };

  const handleDrag = (e) => {
    const { lat: newLat, lng: newLng } = e.target.getLatLng();
    onChange(newLat, newLng);
  };

  return (
    
     <div className={`${isDark ? "dark-map" : "light-map"} relative h-80 w-full border border-border`}>
      <MapContainer
        center={position}
        zoom={zoom}
        scrollWheelZoom
        style={{ height: "100%", width: "100%", background: isDark ? "#0A0A0A" : "#EEE" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url={LIGHT_TILES}
        />
        <ClickHandler onClick={handleClick} />
        <MapCenterUpdater lat={lat} lng={lng} />
        {hasPos && (
          <Marker
            key={`${lat}-${lng}`}
            position={position}
            icon={makeMarkerIcon(isDark)}
            draggable={true}
            eventHandlers={{ dragend: handleDrag }}
          />
        )}
      </MapContainer>
      <button
        type="button"
        onClick={toggleTheme}
        className={`absolute top-2 right-2 z-[1000] flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] tracking-widest uppercase font-bold border transition-all ${
          isDark
            ? "bg-card border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
            : "bg-card border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"
        }`}
        title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      >
        {isDark ? <Sun className="h-3 w-3" /> : <Moon className="h-3 w-3" />}
        {isDark ? "Light" : "Dark"}
      </button>
    </div>
  );
}
